import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, PencilEditIcon, Tick02Icon } from "@hugeicons-pro/core-bulk-rounded";

import { useState } from "react";

import { formatMetric } from "@/lib/format";
import type { MetricUnit } from "@/lib/api/types";

/** Percent targets are stored as fractions (0.04 = 4%); edit in human units. */
const toEdit = (unit: MetricUnit, v: number) => (unit === "percent" ? Math.round(v * 1000) / 10 : v);
const fromEdit = (unit: MetricUnit, v: number) => (unit === "percent" ? v / 100 : v);

/** Inline-editable benchmark target for use inside table cells. Shows the value
 *  with a hover pencil; click opens a small input that saves the RAW (monthly)
 *  target. `value` is the raw stored target; `display` is what to render when
 *  not editing (may be window-scaled). */
export function EditableTarget({
  value, display, unit, money, onSave,
}: {
  value: number | null;
  display: React.ReactNode;
  unit: MetricUnit;
  money?: (v: number | null | undefined) => string;
  onSave: (raw: number) => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  async function commit() {
    const n = Number(draft.replace(",", "."));
    if (!Number.isFinite(n)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(fromEdit(unit, n));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <span className="inline-flex items-center justify-end gap-1">
        {unit === "currency" && <span className="text-xs text-muted-foreground">€</span>}
        <input
          autoFocus type="text" inputMode="decimal" value={draft} disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          className="h-6 w-16 rounded-md border border-input bg-background px-1.5 text-right text-xs tabular-nums outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/24"
        />
        {unit === "percent" && <span className="text-xs text-muted-foreground">%</span>}
        <button type="button" onClick={commit} disabled={saving} aria-label="Opslaan"
          className="grid h-5 w-5 place-items-center rounded text-success hover:bg-success/10 disabled:opacity-50">
          <HugeiconsIcon icon={Tick02Icon} size={13} />
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={saving} aria-label="Annuleren"
          className="grid h-5 w-5 place-items-center rounded text-muted-foreground hover:bg-muted">
          <HugeiconsIcon icon={Cancel01Icon} size={13} />
        </button>
      </span>
    );
  }

  return (
    <span className="group/t inline-flex items-center justify-end gap-1.5">
      <span className="tabular-nums text-muted-foreground">{display}</span>
      <button
        type="button"
        onClick={() => { setDraft(String(value != null ? toEdit(unit, value) : "")); setEditing(true); }}
        aria-label="Benchmark aanpassen"
        className="grid h-5 w-5 place-items-center rounded text-muted-foreground/50 opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/t:opacity-100"
      >
        <HugeiconsIcon icon={PencilEditIcon} size={12} />
      </button>
    </span>
  );
}

/** Kept for callers that want a formatted fallback. */
export const fmtTarget = formatMetric;
